import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Link
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showLoader, dismissLoader, showSuccessMessage, showErroMessage } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import { MultiServiceWorkflowBuilder } from '../../../Components/WorkflowFields';
import workflowService from '../../../services/workflow';
import axios from 'axios';
import AppService from '../../../services/api';

const MultiServiceWorkflowGenerator = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [customerUid, setCustomerUid] = useState('');
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Use the valid token we know works
    const validToken = "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJ0a2FkbWluQHRoYWtsaXMuY29tIiwib3JnIjoiMjU3NDAyMjktODI1Yi00OWMzLThjM2YtZDM0YWQyNDk3ZGM3Iiwicm9sZXMiOlt7ImF1dGhvcml0eSI6IlJPTEVfUm9vdCBBZG1pbiJ9LHsiYXV0aG9yaXR5IjoiUk9MRV9BRE1JTiJ9XSwiZXhwIjoxNzQ3ODkzNjQ0LCJpYXQiOjE3NDc4MDcyNDR9.1K_DUswmYHypX3C_SAb_GuW7hv2bjlw3JcLulDHOeNmDELSpucvUDW8JDtj7po_lQWgXDjmJsoL9EuKbeN6p_A";
    
    // Store the valid token in localStorage so other components can use it
    localStorage.setItem('jwt_token', validToken);
    setToken(validToken);
    console.log('Using valid JWT token for API calls');
    
    // Set the specific customer UID we're testing with
    const testCustomerUid = "fa9c2a52-6c26-44cb-a1f5-559149a88491";
    setCustomerUid(testCustomerUid);
    console.log('Using test customer UID:', testCustomerUid);
    
    // Also check URL params (for admin users creating workflows for specific customers)
    const params = new URLSearchParams(window.location.search);
    const uid = params.get('customerUid');
    if (uid) {
      setCustomerUid(uid);
      console.log('Overriding with URL parameter customer UID:', uid);
    }
  }, []);
  
  // Handle workflow template generation
  const handleWorkflowGenerated = (template) => {
    console.log('🔍 RECEIVED RAW SERVER RESPONSE:', template);
    // Store exactly what the server sent - no transformations
    setGeneratedTemplate(template);
    dispatch(showSuccessMessage('Workflow template generated successfully!'));
  };
  
  const handleSaveTemplate = async () => {
    if (!generatedTemplate) return;
    
    try {
      setLoading(true);
      dispatch(showLoader('Saving workflow template...'));
      
      // Prepare the template data for saving
      const templateData = {
        name: generatedTemplate.name,
        description: generatedTemplate.description,
        version: generatedTemplate.version || 1,
        isActive: false, // Set to inactive by default
        definition: JSON.stringify(generatedTemplate),
        type: generatedTemplate.type || 'new-request',
        variant: generatedTemplate.variant || 'INSIDE'
      };
      
      // Save the workflow template
      const response = await workflowService.createWorkflow(templateData);
      
      console.log('Workflow template saved:', response);
      
      dispatch(dismissLoader());
      dispatch(showSuccessMessage('Workflow template saved successfully'));
      
      // Navigate to the workflows list
      navigate('/workflows');
    } catch (error) {
      console.error('Error saving workflow template:', error);
      setError(`Failed to save workflow template: ${error.message || 'Unknown error'}`);
      dispatch(dismissLoader());
      dispatch(showErroMessage('Failed to save workflow template'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate(-1);
  };
  
  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Multi-Service Workflow Generator
        </Typography>
        
        <Typography variant="body1" color="textSecondary" paragraph>
          Create workflow templates with multiple services and dependencies using our new server-side template generation.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mb: 4 }}>
          <MultiServiceWorkflowBuilder
            token={token}
            customerUid={customerUid}
            onWorkflowGenerated={handleWorkflowGenerated}
          />
        </Paper>
        
        {generatedTemplate && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Generated Workflow Template
            </Typography>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {generatedTemplate.name}
                </Typography>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveTemplate}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Template'}
                </Button>
              </Box>
              
              <Typography variant="body2" color="textSecondary" paragraph>
                {generatedTemplate.description || 'No description provided'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Template Details:
              </Typography>
              
              <Box>
                {/* Basic template information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Basic Information:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2"><strong>Name:</strong> {generatedTemplate.name}</Typography>
                    <Typography variant="body2"><strong>Description:</strong> {generatedTemplate.description}</Typography>
                    <Typography variant="body2"><strong>Type:</strong> {generatedTemplate.definition?.type || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Variant:</strong> {generatedTemplate.definition?.variant || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Customer UID:</strong> {generatedTemplate.definition?.customerUid || 'N/A'}</Typography>
                  </Box>
                </Box>
                
                {/* Steps and field information */}
                {generatedTemplate.definition?.steps && generatedTemplate.definition.steps.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Steps ({generatedTemplate.definition.steps.length}):
                    </Typography>
                    
                    {generatedTemplate.definition.steps.map((step, index) => (
                      <Box key={step.id} sx={{ 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '4px',
                        p: 2,
                        mb: 2,
                        backgroundColor: '#f8f8f8'
                      }}>
                        <Typography variant="subtitle2">
                          Step {index + 1}: {step.name} ({step.id})
                        </Typography>
                        <Typography variant="body2"><strong>Service ID:</strong> {step.serviceId}</Typography>
                        <Typography variant="body2"><strong>Execution Mode:</strong> {step.executionMode}</Typography>
                        <Typography variant="body2"><strong>Required:</strong> {step.required ? 'Yes' : 'No'}</Typography>
                        
                        {/* Display fields */}
                        {step.fields && step.fields.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2"><strong>Regular Fields ({step.fields.length}):</strong></Typography>
                            <Box sx={{ 
                              maxHeight: '200px', 
                              overflow: 'auto',
                              p: 1,
                              pl: 2,
                              border: '1px solid #eaeaea',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff'
                            }}>
                              {step.fields.map((field, fieldIndex) => (
                                <Box key={field.code || `field-${fieldIndex}`} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    {field.label || field.code || `Field ${fieldIndex + 1}`} ({field.type})
                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Display static fields */}
                        {step.staticFields && step.staticFields.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2"><strong>Static Fields ({step.staticFields.length}):</strong></Typography>
                            <Box sx={{ 
                              maxHeight: '200px', 
                              overflow: 'auto',
                              p: 1,
                              pl: 2,
                              border: '1px solid #eaeaea',
                              borderRadius: '4px',
                              backgroundColor: '#ffffff'
                            }}>
                              {step.staticFields.map((field, fieldIndex) => (
                                <Box key={field.code || `static-field-${fieldIndex}`} sx={{ mb: 1 }}>
                                  <Typography variant="body2">
                                    {field.label || field.code || `Static Field ${fieldIndex + 1}`} ({field.type})
                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                
                {/* Raw JSON for reference */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Raw JSON Output:
                  </Typography>
                  <Box sx={{ 
                    maxHeight: '300px', 
                    overflow: 'auto', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '4px',
                    p: 2,
                    backgroundColor: '#f5f5f5'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(generatedTemplate, null, 2)}
                    </pre>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MultiServiceWorkflowGenerator;
